import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/errors';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Loader2, FileCheck2, AlertTriangle, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('lv-LV', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

function InvestorsTab({ language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/investments/admin/list`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        toast.error(formatApiError(e, 'Neizdevās ielādēt investorus'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isLV = language === 'lv';

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/investments/admin/export.csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kolv-investori-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e, isLV ? 'Neizdevās lejupielādēt CSV' : 'CSV download failed'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#D97736]" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="admin-tab-investors">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-[#111]">{isLV ? 'Investori un piekrišanas' : 'Investors & consent'}</h2>
        <button
          onClick={downloadCsv}
          disabled={downloading || data.investor_count === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111] text-white text-sm font-semibold hover:bg-[#333] transition-colors disabled:opacity-50"
          data-testid="investors-export-csv"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {isLV ? 'Lejupielādēt CSV' : 'Download CSV'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-5">
          <p className="text-xs text-[#9CA3AF] uppercase tracking-wide">{isLV ? 'Kopā savākts' : 'Total raised'}</p>
          <p className="text-3xl font-bold text-[#4A6B53]">€{data.grand_total.toFixed(2)}</p>
          <p className="text-xs text-[#9CA3AF] mt-1">{isLV ? 'Mērķis' : 'Goal'}: €{data.goal.toFixed(0)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-5">
          <p className="text-xs text-[#9CA3AF] uppercase tracking-wide">{isLV ? 'Investori' : 'Investors'}</p>
          <p className="text-3xl font-bold text-[#D97736]">{data.investor_count}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-5">
          <p className="text-xs text-[#9CA3AF] uppercase tracking-wide">{isLV ? 'Peļņas dalīšana' : 'Profit share'}</p>
          <p className="text-3xl font-bold text-[#111]">{data.profit_share_pct}%</p>
          <p className="text-xs text-[#9CA3AF] mt-1">{isLV ? 'sadalīts proporcionāli' : 'shared proportionally'}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E4E0] overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-[#F3F2EE] text-xs font-semibold text-[#5C5C5C] uppercase tracking-wide">
          <div className="col-span-4">{isLV ? 'Investors' : 'Investor'}</div>
          <div className="col-span-3 text-right">{isLV ? 'Ieguldīts' : 'Invested'}</div>
          <div className="col-span-3 text-right">{isLV ? 'Daļa no 10%' : 'Share of 10%'}</div>
          <div className="col-span-2 text-right">{isLV ? 'Pirkumi' : 'Purchases'}</div>
        </div>
        {data.investors.length === 0 && (
          <div className="px-5 py-10 text-center text-[#9CA3AF] text-sm" data-testid="no-investors">
            {isLV ? 'Vēl nav investoru.' : 'No investors yet.'}
          </div>
        )}
        {data.investors.map((inv) => (
          <div key={inv.user_id} className="border-t border-[#E5E4E0]">
            <button
              onClick={() => setExpanded(expanded === inv.user_id ? null : inv.user_id)}
              className="w-full grid grid-cols-12 gap-2 px-5 py-4 text-left hover:bg-[#FAFAFA] transition-colors items-center"
              data-testid={`investor-row-${inv.user_id}`}
            >
              <div className="col-span-4 min-w-0">
                <p className="font-semibold text-[#111] truncate">{inv.user_name || '—'}</p>
                <p className="text-xs text-[#9CA3AF] truncate">{inv.user_email}</p>
                {inv.latest_terms_version ? (
                  <span className="mt-1 inline-flex items-center gap-1 text-[11px]" data-testid={`investor-terms-${inv.user_id}`}>
                    {inv.latest_terms_version !== data.current_terms_version
                      ? <AlertTriangle className="w-3 h-3 text-amber-500" />
                      : <FileCheck2 className="w-3 h-3 text-[#4A6B53]" />}
                    <span className={inv.latest_terms_version !== data.current_terms_version ? 'text-amber-600 font-medium' : 'text-[#4A6B53]'}>
                      {isLV ? 'Noteikumi' : 'Terms'} v{inv.latest_terms_version}
                    </span>
                    {inv.first_accepted_at && <span className="text-[#9CA3AF]">· {fmtDateTime(inv.first_accepted_at)}</span>}
                  </span>
                ) : (
                  <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#9CA3AF]" data-testid={`investor-terms-${inv.user_id}`}>
                    {isLV ? 'Nav datu par noteikumiem' : 'No terms record'}
                  </span>
                )}
              </div>
              <div className="col-span-3 text-right font-bold text-[#4A6B53]">€{inv.total.toFixed(2)}</div>
              <div className="col-span-3 text-right text-[#D97736] font-semibold">{inv.profit_share_pct_of_pool}%</div>
              <div className="col-span-2 text-right text-[#5C5C5C] flex items-center justify-end gap-1">
                {inv.purchases.length}
                <ChevronRight className={`w-4 h-4 transition-transform ${expanded === inv.user_id ? 'rotate-90' : ''}`} />
              </div>
            </button>
            {expanded === inv.user_id && (
              <div className="px-5 pb-4 bg-[#FAFAFA]">
                <div className="space-y-2">
                  {inv.purchases.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-white rounded-xl border border-[#E5E4E0] px-4 py-2 gap-3" data-testid={`investor-purchase-${inv.user_id}-${i}`}>
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">{p.label}</Badge>
                          <span className="text-xs text-[#9CA3AF]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString('lv-LV') : ''}</span>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#5C5C5C]">
                          {p.terms_accepted ? <FileCheck2 className="w-3 h-3 text-[#4A6B53]" /> : <AlertTriangle className="w-3 h-3 text-amber-500" />}
                          {p.terms_version
                            ? <>{isLV ? 'Noteikumi' : 'Terms'} v{p.terms_version} · {isLV ? 'piekrita' : 'accepted'} {fmtDateTime(p.terms_accepted_at)}</>
                            : (isLV ? 'Bez noteikumu ieraksta' : 'No terms record')}
                        </span>
                      </div>
                      <span className="font-semibold text-[#111] shrink-0">€{Number(p.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default InvestorsTab;
